import type { Category } from '@scayle/storefront-nuxt'

type RootCategoriesPayload = {
  categories?: Category[]
  activeNode?: Category
}

type RootCategoryRpcOptions = {
  /** @deprecated use immediate instead */
  autoFetch?: boolean
  lazy?: boolean
  immediate?: boolean
}

const shapeShopLevelCustomData = (
  data: Category['shopLevelCustomData'] = {},
) => {
  const shaped: Category['shopLevelCustomData'] = {}

  if (data.HasProducts === false) {
    shaped.HasProducts = false
  }

  if (data.HideForMainNavigation) {
    shaped.HideForMainNavigation = data.HideForMainNavigation
  }

  if (data.HideForPLPNavigation) {
    shaped.HideForPLPNavigation = data.HideForPLPNavigation
  }

  if (data.PLPShowHoverImages) {
    shaped.PLPShowHoverImages = data.PLPShowHoverImages
  }

  return shaped
}

const shapeRootCategoryProperties = (category: Category) => {
  const saleProperties = category.properties?.filter(
    (property) => property.name === 'sale',
  )

  return saleProperties?.length ? saleProperties : undefined
}

const shapeCountryLevelCustomData = (category: Category) =>
  category.countryLevelCustomData?.cmsPagePath
    ? { cmsPagePath: category.countryLevelCustomData.cmsPagePath }
    : undefined

const shapeRootCategoryShopLevelCustomData = (category: Category) => {
  const customData = shapeShopLevelCustomData(category.shopLevelCustomData)

  return Object.keys(customData).length ? customData : undefined
}

const shapeRootCategory = (category: Category): Category => {
  const properties = shapeRootCategoryProperties(category)
  const countryLevelCustomData = shapeCountryLevelCustomData(category)
  const shopLevelCustomData = shapeRootCategoryShopLevelCustomData(category)
  const children = category.children?.map(shapeRootCategory)

  // The RPC deliberately returns a payload-shaped Category subset. Keep the
  // public SDK type for consumers while acknowledging that runtime contract.
  return {
    id: category.id,
    parentId: category.parentId,
    name: category.name,
    path: category.path,
    slug: category.slug,
    depth: category.depth,
    ...(properties ? { properties } : {}),
    ...(countryLevelCustomData ? { countryLevelCustomData } : {}),
    ...(shopLevelCustomData ? { shopLevelCustomData } : {}),
    ...(children?.length ? { children } : {}),
  } as unknown as Category
}

export const shapeRootCategoriesPayload = (
  payload: RootCategoriesPayload,
): RootCategoriesPayload => ({
  ...payload,
  activeNode: payload.activeNode
    ? shapeRootCategory(payload.activeNode)
    : payload.activeNode,
  categories: payload.categories?.map(shapeRootCategory),
})

export default ({
  key = 'useRootCategories',
  params,
  options,
}: {
  key?: string
  params?: any
  options?: RootCategoryRpcOptions
} = {}) => {
  const { data, status, refresh } = useRpc(
    'getRootCategories',
    key,
    {
      ...params,
      with: {
        ...params?.with,
      },
      properties: {
        ...params?.properties,
        withName: ['sale'],
      },
    },
    {
      ...options,
      transform: shapeRootCategoriesPayload,
    },
  )

  const getCategory = (categoryId: number): Category | undefined => {
    const find = (id: number, arr: Category[]): Category | undefined => {
      for (const i of arr) {
        if (i.id === id) return i
        if (i.children?.length) {
          const result = find(id, i.children)
          if (result) return result
        }
      }
    }
    return find(categoryId, data.value?.categories || [])
  }

  const getParentCategory = (categoryId: number): Category | undefined => {
    const parentId = getCategory(categoryId)?.parentId
    return parentId ? getCategory(parentId) : undefined
  }

  return {
    data,
    refresh,
    status,
    getCategory,
    getParentCategory,
  }
}
